import { ArtistJobs } from '@customTypes/types';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Flex,
  Input,
  Radio,
  Space,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useIntl } from 'react-intl';

import { useAddPortfolioItemForm } from '@/features/artist/portfolio/hooks/useAddPortfolioItemForm';
import { COUNTRY_LIST } from '@/shared/data/country';
import { LBModalFullscreenLayout } from '@/shared/layouts';
import {
  LBButton,
  LBDatePicker,
  LBRadio,
  LBSwitcher,
  LBTextField
} from '@/shared/ui-kit/components';
import { ChoseOptionsWithSearch } from '@/shared/view';

export const AddPortfolioItemForm = ({
  onSuccess,
  existUntilNow
}: {
  onSuccess: () => void;
  existUntilNow?: boolean;
}) => {
  const { state, functions } = useAddPortfolioItemForm({ onSuccess });
  // const [opened, { open, close }] = useDisclosure();
  const [opened, { close }] = useDisclosure();
  const { formatMessage } = useIntl();

  return (
    <form onSubmit={functions.onSubmit}>
      <LBModalFullscreenLayout opened={opened} onClose={close}>
        <Flex>
          <ActionIcon color='gray' variant='transparent' onClick={close}>
            <ArrowLeftIcon width={24} />
          </ActionIcon>
        </Flex>
        <Space h={32} />

        <ChoseOptionsWithSearch
          data={COUNTRY_LIST}
          initialValues={state.form.getValues().clubCountry}
          selectElement='radio'
          title={formatMessage({ id: 'commons.clubLocation' })}
          onSelect={(value) => {
            state.form.setFieldValue('clubCountry', value as string);
            close();
          }}
        />
      </LBModalFullscreenLayout>
      <Title order={1}>
        {formatMessage({ id: 'popups.addWorkItem.title' })}
      </Title>
      <Space h={32} />
      <LBTextField
        key={state.form.key('clubName')}
        description={formatMessage({ id: 'form.clubName.description' })}
        disabled={state.form.submitting}
        placeholder={formatMessage({ id: 'form.clubName.label' })}
        {...state.form.getInputProps('clubName')}
      />
      <Space h={16} />
      <Input
        component='select'
        value={state.form.getValues().clubCountry}
        rightSection={
          <img
            alt='arrow down'
            src='/arrow_down.svg'
            style={{ width: 14, height: 14 }}
          />
        }
        styles={{
          input: {
            borderRadius: 8
          }
        }}
        onChange={(event) =>
          state.form.setFieldValue('clubCountry', event.currentTarget.value)
        }
      >
        <option disabled value=''>
          {formatMessage({ id: 'commons.selectCountry' })}
        </option>
        {COUNTRY_LIST.map((country) => (
          <option key={country.value} value={country.value}>
            {country.label}
          </option>
        ))}
      </Input>
      <Text fz={12} mb={4} style={{color:"gray"}}>
        {formatMessage({ id: 'commons.clubLocation' })}
      </Text>

      <Space h={13} />
      <LBDatePicker
        key={state.form.key('startDate')}
        clearable
        disabled={state.form.submitting}
        placeholder={formatMessage({ id: 'form.workPeriod.start.placeholder' })}
        {...state.form.getInputProps('startDate')}
      />
      <Space h={25} />
      <LBDatePicker
        key={state.form.key('endDate')}
        clearable
        excludeDate={functions.excludeDate}
        placeholder={formatMessage({ id: 'form.workPeriod.end.placeholder' })}
        disabled={
          state.form.getValues().untilNow ||
          !state.form.getValues().startDate ||
          state.form.submitting
        }
        {...state.form.getInputProps('endDate')}
      />
      {/* Until Now Toggle */}
      {/* <LBListItem> */}
      <Space h={10} />
      <Text fz={14}>{formatMessage({ id: 'commons.untilNow' })}</Text>
      <LBSwitcher
        key={state.form.key('untilNow')}
        disabled={
          existUntilNow
            ? existUntilNow || state.form.submitting
            : state.form.submitting
        }
        {...state.form.getInputProps('untilNow')}
      />

      {/* Validation Message if needed */}
      {existUntilNow && (
        <>
          <Space h={10} />
          <Text fz={12}>
            {formatMessage({ id: 'popups.addWorkItem.validationMessage' })}
          </Text>
        </>
      )}

      <Space h={32} />
      <Flex direction='column' gap={8}>
        <Text fz={14}>
          {formatMessage({ id: 'popups.addWorkItem.vacancyQuestion' })}
        </Text>
        <Radio.Group
          key={state.form.key('jobRole')}
          {...state.form.getInputProps('jobRole')}
        >
          <LBRadio
            disabled={state.form.submitting}
            label={formatMessage({ id: 'commons.artistJobs.Strip' })}
            value={ArtistJobs.Strip}
          />
          <Space h={8} />
          <LBRadio
            disabled={state.form.submitting}
            label={formatMessage({ id: 'commons.artistJobs.GoGo' })}
            value={ArtistJobs.GoGo}
          />
          <Space h={8} />
          <LBRadio
            disabled={state.form.submitting}
            label={formatMessage({ id: 'commons.artistJobs.Hostess' })}
            value={ArtistJobs.Hostess}
          />
        </Radio.Group>
      </Flex>
      <Space h={32} />
      <LBButton
        fullWidth
        disabled={!state.form.isValid()}
        loading={state.form.submitting}
        type='submit'
      >
        {formatMessage({ id: 'commons.save' })}
      </LBButton>
    </form>
  );
};
